import { ChevronsUpDown, LogOut } from "lucide-react";
import { useCallback } from "react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { userMenuActions } from "@/constants/navbar-actions";
import { useAuth } from "@/features/auth/hooks/use-auth";
import { useUser } from "@/features/auth/hooks/use-user";
import { m } from "@/paraglide/messages.js";

export function NavUser() {
  const { isMobile } = useSidebar();
  const { user, avatarUrl, initials } = useUser();
  const { signOut } = useAuth();

  const handleSignOut = useCallback(() => {
    signOut();
  }, [signOut]);

  const displayAvatar = avatarUrl ?? user.avatarUrl ?? undefined;

  return (
    <SidebarMenu>
      <SidebarMenuItem className="flex">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            {/* Collapses the same way as the nav pills: nothing centers the row, the
              footer's inset is what shrinks, and `duration-200 ease-linear` keeps the
              avatar tracking the panel in a straight line (see nav-group for why
              `justify-center` here would bend that line into a parabola). */}
            <SidebarMenuButton
              size="lg"
              className="cursor-pointer duration-200 ease-linear data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground group-data-[collapsible=icon]:size-9!"
            >
              <Avatar className="h-8 w-8 rounded-lg transition-[width,height] duration-200 ease-linear group-data-[collapsible=icon]:h-9 group-data-[collapsible=icon]:w-9">
                {displayAvatar && <AvatarImage src={displayAvatar} alt={user.firstName || ""} />}
                <AvatarFallback className="rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                  {initials || "U"}
                </AvatarFallback>
              </Avatar>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">
                  {user.firstName} {user.lastName}
                </span>
                <span className="truncate text-xs">{user.email}</span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
            side={isMobile ? "bottom" : "right"}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <Avatar className="h-8 w-8 rounded-lg">
                  {displayAvatar && <AvatarImage src={displayAvatar} alt={user.firstName || ""} />}
                  <AvatarFallback className="rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                    {initials || "U"}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">
                    {user.firstName} {user.lastName}
                  </span>
                  <span className="truncate text-xs">{user.email}</span>
                </div>
              </div>
            </DropdownMenuLabel>

            {userMenuActions.length > 0 && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  {userMenuActions.map((action) => (
                    <action.component key={action.id} />
                  ))}
                </DropdownMenuGroup>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSignOut}>
              <LogOut />
              {m.layout_user_menu_sign_out()}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
